// /commands client + small in-memory cache keyed by siteId.
//
// Agents call `loadCommandsForSite(siteId)` when opening a conversation; the
// cache returns the same array on subsequent reads so the autocomplete renders
// synchronously after the first network round-trip. To keep newly-added
// commands visible WITHOUT a page reload, cache entries go stale after
// COMMANDS_TTL_MS and the slash menu forces a fresh fetch when it opens.

import { apiFetch } from './api';
import type { MacroActions } from './macro';

export interface SiteCommand {
  id: string;
  shortcut: string;
  label: string;
  /** Reply text; may hold {{placeholders}} (lib/macro.ts). */
  body: string;
  sortOrder: number;
  /** null = a team command; set = personal to that agent. */
  ownerAgentId: string | null;
  /** Inbox changes applied when the reply is sent. */
  actions: MacroActions;
}

interface CacheEntry {
  commands: SiteCommand[];
  fetchedAt: number;
}

// How long a cached list is considered fresh. Past this, loadCommandsForSite
// refetches from the DB so admin edits land at runtime (no reload needed).
const COMMANDS_TTL_MS = 15_000;

const cache = new Map<string, CacheEntry>();
const inFlight = new Map<string, Promise<SiteCommand[]>>();

export function getCommandsForSite(siteId: string): SiteCommand[] | null {
  return cache.get(siteId)?.commands ?? null;
}

function fetchAndCache(siteId: string): Promise<SiteCommand[]> {
  const pending = inFlight.get(siteId);
  if (pending) return pending;
  const p = apiFetch<{ commands: SiteCommand[] }>(`/api/sites/${siteId}/commands`)
    .then(({ commands }) => {
      cache.set(siteId, { commands, fetchedAt: Date.now() });
      return commands;
    })
    .catch(() => {
      // Keep any previously-good list rather than blanking it on a transient
      // failure; only seed an empty list if we never had one.
      const existing = cache.get(siteId);
      if (existing) return existing.commands;
      cache.set(siteId, { commands: [], fetchedAt: Date.now() });
      return [];
    })
    .finally(() => {
      inFlight.delete(siteId);
    });
  inFlight.set(siteId, p);
  return p;
}

/**
 * Return the site's commands. Serves a fresh-enough cache synchronously after
 * the first load; refetches from the DB when the cache is stale (older than
 * COMMANDS_TTL_MS) or when `force` is set (e.g. the agent just opened the
 * slash menu and wants the latest).
 */
export async function loadCommandsForSite(
  siteId: string,
  opts: { force?: boolean } = {},
): Promise<SiteCommand[]> {
  const entry = cache.get(siteId);
  const fresh = entry && Date.now() - entry.fetchedAt < COMMANDS_TTL_MS;
  if (entry && fresh && !opts.force) return entry.commands;
  return fetchAndCache(siteId);
}

/** Drop the cached list for a site so the next load refetches from the DB —
 *  called from the admin UI after create/update/delete. */
export function invalidateCommandsCache(siteId: string): void {
  cache.delete(siteId);
}

// ─── Writes ────────────────────────────────────────────────────────────
// Any agent assigned to the site may author/maintain commands (they're a
// shared team resource). These hit the agent router at /api/sites/:siteId,
// which gates on site membership. Each mutation drops the cache so open
// composers pull the fresh list on their next slash-menu open.

export interface CommandInput {
  shortcut: string;
  label: string;
  body: string;
  sortOrder?: number;
  /** Create only: visible to the creating agent alone. */
  personal?: boolean;
  actions?: MacroActions;
}

export async function createCommand(siteId: string, input: CommandInput): Promise<SiteCommand> {
  const { command } = await apiFetch<{ command: SiteCommand }>(`/api/sites/${siteId}/commands`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
  invalidateCommandsCache(siteId);
  return command;
}

export async function updateCommand(
  siteId: string,
  commandId: string,
  patch: Partial<Omit<CommandInput, 'personal'>>,
): Promise<SiteCommand> {
  const { command } = await apiFetch<{ command: SiteCommand }>(
    `/api/sites/${siteId}/commands/${commandId}`,
    { method: 'PATCH', body: JSON.stringify(patch) },
  );
  invalidateCommandsCache(siteId);
  return command;
}

export async function deleteCommand(siteId: string, commandId: string): Promise<void> {
  await apiFetch(`/api/sites/${siteId}/commands/${commandId}`, { method: 'DELETE' });
  invalidateCommandsCache(siteId);
}
